import { DisposableGroup } from '@blocksuite/global/disposable';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { Subject } from 'rxjs';

import type { HistoryStepHold } from '../extension/history/index.js';
import { assertValidBlockXYWHProps } from '../model/block/xywh-validation.js';
import {
  BlockModel,
  type BlockSchemaType,
  type DraftModel,
  type Store,
  toDraftModel,
  toDraftModelWork,
} from '../model/index.js';
import type { Schema } from '../schema/index.js';
import { AssetsManager, type AssetSource } from './assets.js';
import { BaseBlockTransformer } from './base.js';
import type {
  AfterExportPayload,
  AfterImportPayload,
  BeforeExportPayload,
  BeforeImportPayload,
  TransformerMiddleware,
  TransformerSlots,
} from './middleware.js';
import { Slice } from './slice.js';
import type {
  BlobCRUD,
  BlockSnapshot,
  DocCRUD,
  DocSnapshot,
  SliceSnapshot,
} from './type.js';
import {
  BlockSnapshotSchema,
  DocSnapshotSchema,
  SliceSnapshotSchema,
} from './type.js';

export type TransformerOptions = {
  schema: Schema;
  blobCRUD: BlobCRUD;
  docCRUD: DocCRUD;
  middlewares?: TransformerMiddleware[];
  assetSource?: AssetSource;
};

/** How a block whose data an asynchronous owner materializes is inserted. */
export type SnapshotBlockInsertion =
  /** The owner's work is done: `write` adds the block synchronously, so it
   * joins the transaction that writes the other imported blocks. */
  | { readonly kind: 'write'; write(): void }
  /** The owner inserts the block itself (a cut moves its data through the
   * server): it runs after the written blocks, `deferred` when the block
   * arrives later. */
  | {
      readonly kind: 'after';
      /** Writes the block through `hold`: one undo takes it back with the
       * rest of the import. */
      insert(hold: HistoryStepHold): Promise<'inserted' | 'deferred'>;
      /** The import wrote nothing: releases what the preparation reserved. */
      cancel(): void;
    };

/** Optional clipboard port for blocks whose data must be materialized by an
 * asynchronous owner before they exist locally. False leaves the block to the
 * import; normal imports do not install this hook. The parent of the block
 * may not exist yet when it runs, only when the insertion writes. */
export type SnapshotBlockInserter = (input: {
  snapshot: BlockSnapshot;
  draft: DraftModel;
  doc: Store;
  parentId?: string;
  index?: number;
}) => Promise<SnapshotBlockInsertion | false>;

/** A block snapshot converted for insertion, its asynchronous work done. */
export interface PreparedSnapshotBlock {
  /** Writes the block tree without awaiting anything; the caller runs it in
   * the store transaction of everything it pastes. False when it failed and
   * its blocks were removed again. */
  write(): boolean;
  /** Inserts what its owner inserts itself, after `write`; resolves the root
   * block once it exists, null when it failed or arrives later. */
  settle(): Promise<BlockModel | null>;
}

export const SNAPSHOT_BLOCK_INSERTER = 'snapshot-block-inserter';

interface FlatSnapshot {
  snapshot: BlockSnapshot;
  parentId?: string;
  index?: number;
}

interface DraftBlockTreeNode {
  draft: DraftModel;
  snapshot: BlockSnapshot;
  children: Array<DraftBlockTreeNode>;
}

export class Transformer {
  private readonly _adapterConfigs = new Map<string, string>();

  private readonly _transformerConfigs = new Map<string, unknown>();

  private readonly _assetsManager: AssetsManager;

  private readonly _schema: Schema;

  private readonly _docCRUD: DocCRUD;

  private readonly _disposables: DisposableGroup = new DisposableGroup();

  private readonly _slots: TransformerSlots = {
    beforeImport: new Subject<BeforeImportPayload>(),
    afterImport: new Subject<AfterImportPayload>(),
    beforeExport: new Subject<BeforeExportPayload>(),
    afterExport: new Subject<AfterExportPayload>(),
  };

  blockToSnapshot = (
    model: DraftModel | BlockModel
  ): BlockSnapshot | undefined => {
    try {
      const draftModel =
        model instanceof BlockModel ? toDraftModel(model) : model;

      const snapshot = this._blockToSnapshot(draftModel);

      if (!snapshot) {
        return;
      }

      BlockSnapshotSchema.parse(snapshot);

      return snapshot;
    } catch (error) {
      console.error(`Error when transforming block to snapshot:`);
      console.error(error);
      return;
    }
  };

  docToSnapshot = (doc: Store): DocSnapshot | undefined => {
    const work = this.docToSnapshotWork(doc);
    let next = work.next();
    while (!next.done) next = work.next();
    return next.value;
  };

  /** The same native page export, interruptible through the existing Send driver. */
  *docToSnapshotWork(doc: Store): Generator<void, DocSnapshot | undefined> {
    try {
      yield;
      this._slots.beforeExport.next({ type: 'page', page: doc });
      const rootModel = doc.root;
      const meta = this._exportDocMeta(doc);
      if (!rootModel) {
        throw new BlockSuiteError(
          ErrorCode.TransformerError,
          'Root block not found in doc'
        );
      }
      const draft = yield* toDraftModelWork(rootModel);
      const blocks = yield* this._blockToSnapshotWork(draft);
      yield* this._validateBlockSnapshotsWork([blocks]);
      const docSnapshot: DocSnapshot = { type: 'page', meta, blocks };
      this._slots.afterExport.next({
        type: 'page',
        page: doc,
        snapshot: docSnapshot,
      });
      yield;
      // Page metadata and each native block retain their schema validation.
      DocSnapshotSchema.parse({
        ...docSnapshot,
        blocks: { ...blocks, children: [] },
      });
      return docSnapshot;
    } catch (error) {
      console.error('Error when transforming doc to snapshot:');
      console.error(error);
      return;
    }
  }

  sliceToSnapshot = (slice: Slice): SliceSnapshot | undefined => {
    try {
      this._slots.beforeExport.next({
        type: 'slice',
        slice,
      });
      const { content, pageId, workspaceId } = slice.data;
      const contentSnapshot = [];
      for (const block of content) {
        const blockSnapshot = this.blockToSnapshot(block);
        if (!blockSnapshot) {
          return;
        }
        contentSnapshot.push(blockSnapshot);
      }
      const snapshot: SliceSnapshot = {
        type: 'slice',
        workspaceId,
        pageId,
        content: contentSnapshot,
      };
      this._slots.afterExport.next({
        type: 'slice',
        slice,
        snapshot,
      });
      SliceSnapshotSchema.parse(snapshot);

      return snapshot;
    } catch (error) {
      console.error(`Error when transforming slice to snapshot:`);
      console.error(error);
      return;
    }
  };

  snapshotToBlock = async (
    snapshot: BlockSnapshot,
    doc: Store,
    parent?: string,
    index?: number
  ): Promise<BlockModel | undefined> => {
    const prepared = await this.prepareSnapshotToBlock(
      snapshot,
      doc,
      parent,
      index
    );
    if (!prepared) return;
    doc.transact(() => prepared.write());
    return (await prepared.settle()) ?? undefined;
  };

  /**
   * Converts `snapshot` and runs the asynchronous work of its blocks, writing
   * nothing; `write` then inserts it synchronously. Many prepared blocks are
   * written in one transaction: one update per page part instead of one per
   * block.
   */
  prepareSnapshotToBlock = async (
    snapshot: BlockSnapshot,
    doc: Store,
    parent?: string,
    index?: number
  ): Promise<PreparedSnapshotBlock | undefined> => {
    this._assertValidBlockSnapshotXYWH(snapshot);
    try {
      BlockSnapshotSchema.parse(snapshot);
      this._triggerBeforeImportEvent(snapshot, parent, index);
      const flatSnapshots: FlatSnapshot[] = [];
      this._flattenSnapshot(snapshot, flatSnapshots, parent, index);
      const tree = await this._convertFlatSnapshots(flatSnapshots);
      const insertions = await this._prepareInsertions(
        [tree],
        doc,
        parent,
        index
      );
      const prepared = this._preparedBlocks(
        [tree],
        insertions,
        doc,
        parent,
        index
      );
      return {
        write: prepared.write,
        settle: async () => (await prepared.settle())[0],
      };
    } catch (error) {
      console.error(`Error when transforming snapshot to block:`);
      console.error(error);
      return;
    }
  };

  snapshotToDoc = async (snapshot: DocSnapshot): Promise<Store | undefined> => {
    this._assertValidBlockSnapshotXYWH(snapshot.blocks);
    try {
      this._slots.beforeImport.next({
        type: 'page',
        snapshot,
      });
      DocSnapshotSchema.parse(snapshot);
      const { meta, blocks } = snapshot;
      const doc = this.docCRUD.create(meta.id);
      doc.load();
      await this.snapshotToBlock(blocks, doc);
      this._slots.afterImport.next({
        type: 'page',
        snapshot,
        page: doc,
      });

      return doc;
    } catch (error) {
      console.error(`Error when transforming snapshot to doc:`);
      console.error(error);
      return;
    }
  };

  snapshotToModelData = async (snapshot: BlockSnapshot) => {
    this._assertValidBlockSnapshotXYWH(snapshot);
    try {
      const { children, flavour, props, id } = snapshot;

      const schema = this._getSchema(flavour);
      const snapshotLeaf = {
        id,
        flavour,
        props,
      };
      const transformer = this._getTransformer(schema);
      const modelData = await transformer.fromSnapshot({
        json: snapshotLeaf,
        assets: this._assetsManager,
        children,
      });

      return modelData;
    } catch (error) {
      console.error(
        `Error when transforming snapshot to model data [flavour=${snapshot.flavour}, id=${snapshot.id}]:`,
        error instanceof Error ? error.message : error,
        error instanceof Error ? error.stack : ''
      );
      return;
    }
  };

  snapshotToSlice = async (
    snapshot: SliceSnapshot,
    doc: Store,
    parent?: string,
    index?: number
  ): Promise<Slice | undefined> => {
    snapshot.content.forEach(block => {
      this._assertValidBlockSnapshotXYWH(block);
    });
    try {
      SliceSnapshotSchema.parse(snapshot);

      this._slots.beforeImport.next({
        type: 'slice',
        snapshot,
      });

      const { content, workspaceId, pageId } = snapshot;

      // Create a temporary root snapshot to encompass all content blocks
      const tmpRootSnapshot: BlockSnapshot = {
        id: 'temporary-root',
        flavour: 'affine:page',
        props: {},
        type: 'block',
        children: content,
      };

      for (const block of content) {
        this._triggerBeforeImportEvent(block, parent, index);
      }
      const flatSnapshots: FlatSnapshot[] = [];
      this._flattenSnapshot(tmpRootSnapshot, flatSnapshots, parent, index);

      const blockTree = await this._convertFlatSnapshots(flatSnapshots);
      const first = content[0];

      const deferred = new Set<string>();
      // check if the slice is already in the doc
      if (first && doc.hasBlock(first.id)) {
        // if the slice is already in the doc, we need to move the blocks instead of adding them
        const inserter = this._transformerConfigs.get(
          SNAPSHOT_BLOCK_INSERTER
        ) as SnapshotBlockInserter | undefined;
        const handled = new Set<string>();
        if (inserter) {
          for (const [offset, tree] of blockTree.children.entries()) {
            if (!doc.hasBlock(tree.draft.id)) {
              const skipped = await this._importBlockTrees(
                [tree],
                doc,
                parent,
                index !== undefined ? index + offset : undefined
              );
              skipped.forEach(id => deferred.add(id));
              handled.add(tree.draft.id);
              continue;
            }
            const insertion = await inserter({
              snapshot: tree.snapshot,
              draft: tree.draft,
              doc,
              parentId: parent,
              index: index !== undefined ? index + offset : undefined,
            });
            if (!insertion) continue;
            handled.add(tree.draft.id);
            if (insertion.kind === 'write') insertion.write();
            // Blocks already in the document move: no step of a new import.
            else if ((await insertion.insert(write => write())) === 'deferred')
              deferred.add(tree.draft.id);
          }
        }
        const models = content
          .filter(block => !handled.has(block.id))
          .map(block => doc.getBlock(block.id)?.model)
          .filter(Boolean) as BlockModel[];
        const parentModel = parent ? doc.getBlock(parent)?.model : undefined;
        if (!parentModel) {
          throw new BlockSuiteError(
            ErrorCode.TransformerError,
            'Parent block not found in doc when moving slice'
          );
        }
        const targetSibling =
          index !== undefined ? parentModel.children[index] : null;
        if (models.length) doc.moveBlocks(models, parentModel, targetSibling);
      } else {
        const skipped = await this._importBlockTrees(
          blockTree.children,
          doc,
          parent,
          index
        );
        skipped.forEach(id => deferred.add(id));
      }

      const contentBlocks = blockTree.children
        .filter(tree => !deferred.has(tree.draft.id))
        .map(tree => doc.getModelById(tree.draft.id))
        .filter((x): x is BlockModel => x !== null)
        .map(model => toDraftModel(model));

      const slice = new Slice({
        content: contentBlocks,
        workspaceId,
        pageId,
      });

      this._slots.afterImport.next({
        type: 'slice',
        snapshot,
        slice,
      });

      return slice;
    } catch (error) {
      console.error(`Error when transforming snapshot to slice:`);
      console.error(error);
      return;
    }
  };

  walk = (snapshot: DocSnapshot, callback: (block: BlockSnapshot) => void) => {
    const walk = (block: BlockSnapshot) => {
      try {
        callback(block);
      } catch (error) {
        console.error(`Error when walking snapshot:`);
        console.error(error);
      }

      if (block.children) {
        block.children.forEach(walk);
      }
    };

    walk(snapshot.blocks);
  };

  get adapterConfigs() {
    return this._adapterConfigs;
  }

  get assets() {
    return this._assetsManager.getAssets();
  }

  get assetsManager() {
    return this._assetsManager;
  }

  get schema() {
    return this._schema;
  }

  get docCRUD() {
    return this._docCRUD;
  }

  constructor({
    blobCRUD,
    schema,
    docCRUD,
    middlewares = [],
    assetSource,
  }: TransformerOptions) {
    this._assetsManager = new AssetsManager({
      blob: blobCRUD,
      source: assetSource,
    });
    this._schema = schema;
    this._docCRUD = docCRUD;

    middlewares.forEach(middleware => {
      const cleanup = middleware({
        slots: this._slots,
        docCRUD: this._docCRUD,
        assetsManager: this._assetsManager,
        adapterConfigs: this._adapterConfigs,
        transformerConfigs: this._transformerConfigs,
      });
      if (cleanup) {
        this._disposables.add(cleanup);
      }
    });
  }

  /** The same native export, consumed synchronously by existing callers. */
  private _blockToSnapshot(model: DraftModel): BlockSnapshot | null {
    const work = this._blockToSnapshotWork(model);
    let next = work.next();
    while (!next.done) next = work.next();
    return next.value;
  }

  private *_blockToSnapshotWork(
    model: DraftModel
  ): Generator<void, BlockSnapshot> {
    const begin = function* (this: Transformer, model: DraftModel) {
      this._slots.beforeExport.next({ type: 'block', model });
      const schema = this._getSchema(model.flavour);
      const transformer = this._getTransformer(schema);
      const leaf = yield* transformer.toSnapshotWork({
        model,
        assets: this._assetsManager,
      });
      return {
        model,
        leaf,
        children: model.children,
        childCount: model.children.length,
        index: 0,
        snapshots: [] as BlockSnapshot[],
      };
    };
    yield;
    const stack = [yield* begin.call(this, model)];
    let result: BlockSnapshot | undefined;
    while (stack.length) {
      const frame = stack[stack.length - 1];
      if (frame.index < frame.childCount) {
        yield;
        stack.push(yield* begin.call(this, frame.children[frame.index++]));
        continue;
      }
      yield;
      const snapshot: BlockSnapshot = {
        type: 'block',
        ...frame.leaf,
        children: frame.snapshots,
      };
      this._slots.afterExport.next({
        type: 'block',
        model: frame.model,
        snapshot,
      });
      stack.pop();
      if (stack.length) stack[stack.length - 1].snapshots.push(snapshot);
      else result = snapshot;
    }
    return result!;
  }

  /** Send consumes native export and validation through its existing read driver. */
  *sliceToSnapshotWork(
    slice: Slice
  ): Generator<void, SliceSnapshot | undefined> {
    try {
      yield;
      this._slots.beforeExport.next({ type: 'slice', slice });
      const { content, pageId, workspaceId } = slice.data;
      const snapshots: BlockSnapshot[] = [];
      for (const block of content) {
        yield;
        snapshots.push(yield* this._blockToSnapshotWork(block));
      }
      const snapshot: SliceSnapshot = {
        type: 'slice',
        workspaceId,
        pageId,
        content: snapshots,
      };
      this._slots.afterExport.next({ type: 'slice', slice, snapshot });
      // Same schema/objects; validate each node once instead of recursively
      // cloning the complete subtree inside one non-interruptible parse.
      SliceSnapshotSchema.parse({
        ...snapshot,
        content: Array.isArray(snapshot.content) ? [] : snapshot.content,
      });
      yield* this._validateBlockSnapshotsWork(snapshot.content);
      return snapshot;
    } catch (error) {
      console.error('Error when transforming slice to snapshot:');
      console.error(error);
      return;
    }
  }

  private *_validateBlockSnapshotsWork(
    content: BlockSnapshot[]
  ): Generator<void, void> {
    const stack = [{ children: content, index: 0 }];
    while (stack.length) {
      const frame = stack[stack.length - 1];
      if (frame.index >= frame.children.length) {
        stack.pop();
        continue;
      }
      yield;
      const block = frame.children[frame.index++];
      BlockSnapshotSchema.parse({
        ...block,
        children: Array.isArray(block.children) ? [] : block.children,
      });
      stack.push({ children: block.children, index: 0 });
    }
  }

  private _assertValidBlockSnapshotXYWH(snapshot: BlockSnapshot) {
    assertValidBlockXYWHProps({
      blockId: snapshot.id,
      flavour: snapshot.flavour,
      props: snapshot.props,
      // Imports author documents; only a projection store is unbounded.
      enforceWorld: true,
    });

    snapshot.children.forEach(child => {
      this._assertValidBlockSnapshotXYWH(child);
    });
  }

  private async _convertFlatSnapshots(flatSnapshots: FlatSnapshot[]) {
    // Phase 1: Convert snapshots to draft models in series
    // This is not time-consuming, this is faster than Promise.all
    const draftModels = [];
    for (const flat of flatSnapshots) {
      const draft = await this._convertSnapshotToDraftModel(flat);
      if (draft) {
        draft.id = flat.snapshot.id;
      }
      draftModels.push({
        draft,
        snapshot: flat.snapshot,
        parentId: flat.parentId,
        index: flat.index,
      });
    }

    // Phase 2: Filter out the models that failed to convert
    const validDraftModels = draftModels.filter(item => !!item.draft) as {
      draft: DraftModel;
      snapshot: BlockSnapshot;
      parentId?: string;
      index?: number;
    }[];

    // Phase 3: Rebuild the block trees
    const blockTree = this._rebuildBlockTree(validDraftModels);
    return blockTree;
  }

  private async _convertSnapshotToDraftModel(
    flat: FlatSnapshot
  ): Promise<DraftModel | undefined> {
    try {
      const { children, flavour } = flat.snapshot;
      const schema = this._getSchema(flavour);
      const transformer = this._getTransformer(schema);
      const { props } = await transformer.fromSnapshot({
        json: {
          id: flat.snapshot.id,
          flavour: flat.snapshot.flavour,
          props: flat.snapshot.props,
        },
        assets: this._assetsManager,
        children,
      });

      return {
        id: flat.snapshot.id,
        flavour: flat.snapshot.flavour,
        children: [],
        props,
      } as unknown as DraftModel;
    } catch (error) {
      console.error(`Error when transforming snapshot to model data:`);
      console.error(error);
      return;
    }
  }

  private _exportDocMeta(doc: Store): DocSnapshot['meta'] {
    const docMeta = doc.meta;

    if (!docMeta) {
      throw new BlockSuiteError(
        ErrorCode.TransformerError,
        'Doc meta not found'
      );
    }
    return {
      id: docMeta.id,
      title: docMeta.title,
      createDate: docMeta.createDate,
      tags: [], // for backward compatibility
    };
  }

  private _flattenSnapshot(
    snapshot: BlockSnapshot,
    flatSnapshots: FlatSnapshot[],
    parentId?: string,
    index?: number
  ) {
    flatSnapshots.push({ snapshot, parentId, index });
    if (snapshot.children) {
      snapshot.children.forEach((child, idx) => {
        this._flattenSnapshot(child, flatSnapshots, snapshot.id, idx);
      });
    }
  }

  private _getSchema(flavour: string) {
    const schema = this.schema.flavourSchemaMap.get(flavour);
    if (!schema) {
      throw new BlockSuiteError(
        ErrorCode.TransformerError,
        `Flavour schema not found for ${flavour}`
      );
    }
    return schema;
  }

  private _getTransformer(schema: BlockSchemaType) {
    return (
      schema.transformer?.(this._transformerConfigs) ??
      new BaseBlockTransformer(this._transformerConfigs)
    );
  }

  /**
   * Writes `nodes` under `parent` in one transaction, then the blocks their
   * owners insert; the IDs of the nodes that were not inserted. Throws when
   * the write failed, after removing what it wrote.
   */
  private async _importBlockTrees(
    nodes: DraftBlockTreeNode[],
    doc: Store,
    parentId?: string,
    index?: number
  ): Promise<Set<string>> {
    const insertions = await this._prepareInsertions(
      nodes,
      doc,
      parentId,
      index
    );
    const prepared = this._preparedBlocks(
      nodes,
      insertions,
      doc,
      parentId,
      index
    );
    let written = false;
    doc.transact(() => {
      written = prepared.write();
    });
    const models = await prepared.settle();
    if (!written)
      throw new BlockSuiteError(
        ErrorCode.TransformerError,
        'The blocks of the snapshot could not be written'
      );
    return new Set(
      nodes.filter((_, at) => !models[at]).map(node => node.draft.id)
    );
  }

  private _rebuildBlockTree(
    draftModels: {
      draft: DraftModel;
      snapshot: BlockSnapshot;
      parentId?: string;
      index?: number;
    }[]
  ): DraftBlockTreeNode {
    const nodeMap = new Map<string, DraftBlockTreeNode>();
    // First pass: create nodes and add them to the map
    draftModels.forEach(({ draft, snapshot }) => {
      nodeMap.set(draft.id, { draft, snapshot, children: [] });
    });
    const root = nodeMap.get(draftModels[0].draft.id) as DraftBlockTreeNode;

    // Second pass: build the tree structure
    draftModels.forEach(({ draft, parentId, index }) => {
      const node = nodeMap.get(draft.id);
      if (!node) return;

      if (parentId) {
        const parentNode = nodeMap.get(parentId);
        if (parentNode && index !== undefined) {
          parentNode.children[index] = node;
        }
      }
    });

    if (!root) {
      throw new Error('No root node found in the tree');
    }

    return root;
  }

  private _afterBlockImport(doc: Store, node: DraftBlockTreeNode) {
    const model = doc.getBlock(node.draft.id)?.model;
    if (!model) {
      throw new BlockSuiteError(
        ErrorCode.TransformerError,
        `Block not found by id ${node.draft.id}`
      );
    }
    this._slots.afterImport.next({
      type: 'block',
      model,
      snapshot: node.snapshot,
    });
  }

  /** The insertions of the owners of the blocks of `roots`, prepared at once. */
  private async _prepareInsertions(
    roots: DraftBlockTreeNode[],
    doc: Store,
    parentId?: string,
    index?: number
  ): Promise<Map<string, SnapshotBlockInsertion>> {
    const ids: string[] = [];
    const collect = (node: DraftBlockTreeNode) => {
      ids.push(node.draft.id);
      node.children.forEach(collect);
    };
    roots.forEach(collect);
    await doc.pageStructure.loadMissingPartsOf(ids);
    const insertions = new Map<string, SnapshotBlockInsertion>();
    const insert = this._transformerConfigs.get(SNAPSHOT_BLOCK_INSERTER) as
      | SnapshotBlockInserter
      | undefined;
    if (!insert) return insertions;
    const pending: Promise<void>[] = [];
    const visit = (node: DraftBlockTreeNode, parent?: string, at?: number) => {
      pending.push(
        insert({
          snapshot: node.snapshot,
          draft: node.draft,
          doc,
          parentId: parent,
          index: at,
        }).then(insertion => {
          if (insertion) insertions.set(node.draft.id, insertion);
        })
      );
      node.children.forEach(child => visit(child, node.draft.id));
    };
    roots.forEach((root, offset) =>
      visit(root, parentId, index === undefined ? index : index + offset)
    );
    const failed = (await Promise.allSettled(pending)).find(
      result => result.status === 'rejected'
    );
    if (failed) {
      // A failed owner leaves nothing reserved: the prepared ones release
      // their reservation before the paste fails.
      for (const insertion of insertions.values())
        if (insertion.kind === 'after') insertion.cancel();
      throw failed.reason;
    }
    return insertions;
  }

  private _preparedBlocks(
    roots: DraftBlockTreeNode[],
    insertions: ReadonlyMap<string, SnapshotBlockInsertion>,
    doc: Store,
    parentId?: string,
    index?: number
  ): {
    write(): boolean;
    settle(): Promise<(BlockModel | null)[]>;
  } {
    // Blocks whose owner inserts them, with their subtrees: after the write.
    const after: Array<{
      node: DraftBlockTreeNode;
      insert(hold: HistoryStepHold): Promise<'inserted' | 'deferred'>;
    }> = [];
    const visit = (
      inserted: string[],
      node: DraftBlockTreeNode,
      parent?: string,
      at?: number
    ) => {
      const { id, flavour, props } = node.draft;
      const insertion = insertions.get(id);
      if (insertion?.kind === 'after') {
        after.push({ node, insert: hold => insertion.insert(hold) });
        return;
      }
      if (!doc.hasBlock(id)) inserted.push(id);
      if (insertion) insertion.write();
      else doc.addBlock(flavour, { id, ...props }, parent, at);
      written.push(node);
      node.children.forEach(child => visit(inserted, child, id));
    };
    // The models of written blocks exist once the transaction ended.
    const written: DraftBlockTreeNode[] = [];
    const imported = () =>
      written.splice(0).forEach(node => this._afterBlockImport(doc, node));
    // Writes `nodes` under `parent`; on failure the new IDs to remove once the
    // transaction ended, so other pasted blocks and edits of other actors
    // survive.
    const writeAll = (
      nodes: DraftBlockTreeNode[],
      parent?: string,
      at?: number
    ): string[] | null => {
      const inserted: string[] = [];
      try {
        nodes.forEach((node, offset) =>
          visit(inserted, node, parent, at === undefined ? at : at + offset)
        );
        return null;
      } catch (error) {
        console.error(`Error when transforming snapshot to block:`);
        console.error(error);
        return inserted;
      }
    };
    // Outside the transaction: each delete publishes its child-list update
    // before the parent is tested. A container another actor added a child to
    // stays.
    const remove = (inserted: string[]) => {
      for (const id of inserted.reverse()) {
        const model = doc.getBlock(id)?.model;
        if (model && !model.children.length)
          doc.deleteBlock(id, { deleteChildren: false });
      }
    };
    let failed: string[] | null | undefined;
    const write = (): boolean => {
      failed = writeAll(roots, parentId, index);
      return failed === null;
    };
    const started = new Set<string>();
    // Roots whose owner failed or deferred them: no model of this paste.
    const skipped = new Set<DraftBlockTreeNode>();
    const run = async (): Promise<void> => {
      if (failed !== null) {
        if (failed) remove(failed);
        roots.forEach(root => skipped.add(root));
        return;
      }
      imported();
      // What owners write later belongs to the step of this import.
      const hold = doc.history.holdStep();
      // An owner's block arrives first, then its prepared subtree; a nested
      // owner's block joins `after` while this loop runs.
      for (const { node, insert } of after) {
        started.add(node.draft.id);
        let result: 'inserted' | 'deferred';
        try {
          result = await insert(hold);
        } catch (error) {
          console.error(`Error when transforming snapshot to block:`);
          console.error(error);
          if (roots.includes(node)) skipped.add(node);
          continue;
        }
        if (result === 'deferred') {
          if (roots.includes(node)) skipped.add(node);
          continue;
        }
        this._afterBlockImport(doc, node);
        let children!: string[] | null;
        hold(() =>
          doc.transact(() => {
            children = writeAll(node.children, node.draft.id);
          })
        );
        if (children) {
          written.length = 0;
          hold(() => remove(children!));
          if (roots.includes(node)) skipped.add(node);
          continue;
        }
        imported();
      }
    };
    const settle = async (): Promise<(BlockModel | null)[]> => {
      try {
        await run();
        return roots.map(root =>
          skipped.has(root)
            ? null
            : (doc.getBlock(root.draft.id)?.model ?? null)
        );
      } finally {
        // What was prepared but never inserted releases its reservation.
        for (const [id, insertion] of insertions)
          if (insertion.kind === 'after' && !started.has(id))
            insertion.cancel();
      }
    };
    return { write, settle };
  }

  private _triggerBeforeImportEvent(
    snapshot: BlockSnapshot,
    parent?: string,
    index?: number
  ) {
    const traverseAndTrigger = (
      node: BlockSnapshot,
      parent?: string,
      index?: number
    ) => {
      this._slots.beforeImport.next({
        type: 'block',
        snapshot: node,
        parent: parent,
        index: index,
      });
      if (node.children) {
        node.children.forEach((child, idx) => {
          traverseAndTrigger(child, node.id, idx);
        });
      }
    };
    traverseAndTrigger(snapshot, parent, index);
  }

  reset() {
    this._assetsManager.cleanup();
  }

  [Symbol.dispose]() {
    this._disposables.dispose();
    this._assetsManager.cleanup();
  }
}
